import { ListStore, memone } from '@deqo/foundation';
import { injectable } from 'inversify';
import { assertNot } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Connector, resolve } from '../../Container';
import { EditorSession } from '../EditorSession';
import { MyRoleInfo } from '../MyRoleInfo';
import { ToolSettings } from './ToolBehavior';
import { toolEquip } from './ToolCommands';
import { ToolDefinition } from './ToolDefinition';
import { PrevToolInfo, ToolInfo } from './ToolInfo';

export interface ToolRecord extends ToolDefinition {
    enabled: boolean;
    visible: boolean;
}

@injectable()
export class ToolManager extends ListStore<Readonly<ToolRecord>> {

    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(EditorSession) private session!: EditorSession;

    private settings = new Map<string, ToolSettings>();

    private get index() {
        return this.produceIndex(this.getState());
    }

    protected start() {
        this.burden(
            this.observe(this.handleDefaultToolSpecifics),
            this.session.observe(ToolInfo, this.handlePrevToolTracking),
        );
        this.handleDefaultToolSpecifics();
    }

    public get(id: string): ToolRecord {
        if (id === 'default') {
            return this.get(this.isEnabled('pointerTool') ? 'pointerTool' : 'handTool');
        }
        return this.index.get(id) ?? null;
    }

    public has(id: string) {
        return this.index.has(id);
    }

    public register(...toolDefs: ToolDefinition[]) {
        this.updateState(s => {
            for (const td of toolDefs) {
                assertNot(this.has(td.id), `ToolRegister.add: ${td.id} already registered.`);
                s.push(Object.freeze({ ...td, enabled: true, visible: true }));
            }
        });
    }

    public isEnabled(id: string) {
        if (id === 'default') return true;
        const { canParticipate } = this.session.get(MyRoleInfo);
        if (!canParticipate) return false;
        const tool = this.get(id);
        return !!tool?.enabled || !!tool?.special;
    }

    public isVisible(id: string) {
        if (id === 'default') return true;
        const { canParticipate } = this.session.get(MyRoleInfo);
        return canParticipate && !!this.get(id)?.visible;
    }

    public setEnabled(list: '*' | readonly string[]) {
        list = this.normalizeEnabledList(list);
        const enabled = Object.fromEntries(this.map(t => [t.id, !t.icon || list.includes(t.id)]));
        this.updateState(s => {
            for (let i = 0; i < s.length; i++) {
                const { id } = s[i];
                if (id in enabled) {
                    s[i] = { ...s[i], enabled: enabled[id] };
                }
            }
        });
        return this;
    }

    public setVisible(list: '*' | readonly string[]) {
        list = this.normalizeEnabledList(list);
        const visible = Object.fromEntries(this.map(t => [t.id, !t.icon || list.includes(t.id)]));
        this.updateState(s => {
            for (let i = 0; i < s.length; i++) {
                const { id } = s[i];
                if (id in visible) {
                    s[i] = { ...s[i], visible: visible[id] };
                }
            }
        });
    }

    public getSetting(toolId: string, key: string) {
        return (this.getSettings(toolId) ?? {})[key] ?? null;
    }

    public getSettings(toolId: string): ToolSettings {
        return this.settings.get(toolId) ?? null;
    }

    public setSetting(toolId: string, key: string, value: any): this {
        this.setSettings(toolId, { [key]: value });
        return this;
    }

    public setSettings(toolId: string, values: ToolSettings): this {
        this.settings.set(toolId, { ...(this.getSettings(toolId) ?? {}), ...(values ?? {}) });
        return this;
    }

    public equip(toolId: string, settings?: ToolSettings): this {
        if (settings) {
            this.setSettings(toolId, settings);
        }
        this.dispatch(toolEquip, [toolId]);
        return this;
    }

    private normalizeEnabledList(list: '*' | readonly string[]) {
        if (list === '*') return this.map(x => x.id);
        // If specific list, auto enable pointer if any of the mutating categories are present
        const hit = list.map(x => this.get(x)).find(x => ['collaboration', 'design'].includes(x?.category));
        return (!!hit ? ['pointerTool'] : []).concat(list.filter(x => !!x));
    }

    private normalizeVisibleList(list: '*' | readonly string[]) {
        if (list === '*') return this.map(x => x.id);
        return list;
    }

    private handleDefaultToolSpecifics = () => {
        const active = this.session.get(ToolInfo);
        // If active is handTool and pointerTool available, switch...
        if (active.id === 'handTool' && this.isEnabled('pointerTool')) {
            this.dispatch(toolEquip, ['default']);
        }
        else if (!this.isEnabled(active.id)) {
            this.dispatch(toolEquip, ['default']);
        }
    };

    private handlePrevToolTracking = (_next: ToolInfo, prev: ToolInfo) => {
        if (prev.id === 'handTool' || prev.id === 'pointerTool') return;
        this.session.update(PrevToolInfo, prev);
    };

    @memone()
    private produceIndex(state: ToolRecord[]) {
        return new Map(state.map(x => [x.id, x]));
    }
}

export const inTool = (toolDef: ToolDefinition): Connector => r => void (r.call(ToolManager).register(toolDef));
export const inTools = (...toolDefs: ToolDefinition[]): Connector => r => void (r.call(ToolManager).register(...toolDefs));
